import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-sm font-medium transition-[background-color,border-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-input bg-background shadow-sm hover:border-foreground/20 hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "shadow-none hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5 py-2 max-md:min-h-11",
        sm: "h-8 px-3 max-md:min-h-11",
        lg: "h-10 px-6 max-md:min-h-11",
        icon: "h-9 w-9 max-md:h-11 max-md:w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}

function blockClick(event: React.MouseEvent<HTMLElement>) {
  event.preventDefault();
  event.stopPropagation();
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className, variant, size, asChild = false,
    loading = false, loadingLabel = "Working…",
    disabled, children, onClick, onClickCapture, ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const unavailable = disabled || loading;
    const pendingContent = <>
      <LoaderCircle className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      <span className={size === "icon" ? "sr-only" : undefined}>{loadingLabel}</span>
    </>;
    let content = loading ? pendingContent : children;
    if (asChild && React.isValidElement<{
      children?: React.ReactNode;
      onClick?: React.MouseEventHandler<HTMLElement>;
      onClickCapture?: React.MouseEventHandler<HTMLElement>;
    }>(children)) {
      const guards = unavailable ? { onClick: blockClick, onClickCapture: blockClick } : undefined;
      content = React.cloneElement(children, guards, loading ? pendingContent : children.props.children);
    }
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={asChild ? undefined : unavailable}
        aria-disabled={asChild && unavailable ? true : props["aria-disabled"]}
        aria-busy={loading ? true : props["aria-busy"]}
        aria-label={loading && props["aria-label"] ? `${props["aria-label"]} — ${loadingLabel}` : props["aria-label"]}
        onClickCapture={(event) => {
          if (unavailable) {
            blockClick(event);
            return;
          }
          onClickCapture?.(event);
        }}
        onClick={(event) => {
          if (unavailable) {
            blockClick(event);
            return;
          }
          onClick?.(event);
        }}
      >{content}</Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
